/*
 Developed by CQ Inversiones SAS. Copyright ©. 2019 - 2024. All rights reserved.
 Desarrollado por CQ Inversiones SAS. Copyright ©. 2019 - 2024. Todos los derechos reservados.
 */

/*
 * Project:      mkad-frontend
 * Developed by: Juan David Pelaez Cumbe
 * Date:         27/04/24 - 18:27
 * Module name:  app.config
 * File name:    app.config.ts
 * IDE:          WebStorm
 */

import {ApplicationConfig, importProvidersFrom, LOCALE_ID} from '@angular/core';
import {provideRouter} from '@angular/router';
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from '@angular/common/http';
import {provideAnimations} from '@angular/platform-browser/animations';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {
  NbDatepickerModule,
  NbDateService,
  NbDialogModule,
  NbMenuModule,
  NbNativeDateService,
  NbSidebarModule,
  NbSidebarService,
  NbThemeModule,
  NbToastrModule,
  NbToastrService,
} from '@nebular/theme';
import {NbMomentDateModule} from '@nebular/moment';
import {NbDateFnsDateModule, NbDateFnsDateService} from '@nebular/date-fns';
import {NbSecurityModule} from '@nebular/security';
import {NbEvaIconsModule} from '@nebular/eva-icons';
import {NgxZibanuLibModule, ZbSecureStorageService} from '@zibanu/lib';
import {
  NgxZibanuAuthModule,
  ZbAuthManagerService,
  ZbPasswordAuthStrategy,
  ZbAuthJWTToken,
  ZbAuthUserService, ZbAuthLangInterceptor, ZbAuthJWTInterceptor,
} from '@zibanu/auth';
import {NgxZibanuUiModule, ValidatorsManager} from '@zibanu/ui';
import {provideEcharts} from 'ngx-echarts';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {NgxDatatableModule} from '@swimlane/ngx-datatable';
import {LeafletModule} from '@asymmetrik/ngx-leaflet';
import {
  MkadBaseService,
  MkadMenuManagerService,
  PreferencesService,
  MkadUserCustomersService,
  URLS_SERVICE,
} from '@shared/services';
import {SessionManagerInterceptor} from '@shared/utils/interceptors/sesion-manager.interceptor';
import {DEFAULT_MAP_LATITUDE, DEFAULT_MAP_LONGITUDE, SETTING_TIMEOUT} from '@shared/constants';
import {environment} from '../environments/environment';
import {APP_ROUTES} from './app.routes';
import {DEFAULT_DIAL_CODE} from '@core/tokens/default-dial-code';
import {DEFAULT_OWNER_URL} from '@core/tokens/default-owner-url';
import {DEFAULT_APP_SETTINGS_CONFIG} from '@core/tokens/default-app-settings-config.token';
import {DEFAULT_ABOUT_US_DATA} from '@core/tokens/default-about-us-data.token';
import {FormatTypeExportEnum, ManageNullSettingEnum} from '@shared/enums';
import {MkadLocaleService} from '@shared/services/mkad-locale.service';
import {SupportSessionInterceptor} from '@auth/interceptors/support-session.interceptor';
import {BACKGROUND_URL, LOGO_URL} from '@core/constants/bg-images.const';

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

import {provideEchartsCore} from 'ngx-echarts';
import * as echarts from 'echarts/core';
import {PieChart, BarChart } from 'echarts/charts';
import {CanvasRenderer} from 'echarts/renderers';
import {LegendComponent, TooltipComponent, GridComponent } from 'echarts/components';
import {EXPIRED_MANAGE_TOKEN} from '@core/tokens/expired-manage-token';

echarts.use([PieChart, BarChart, CanvasRenderer, TooltipComponent, LegendComponent, GridComponent]);

/**
 * nebular root module
 */
export const NEBULAR_MODULES = [
  NbThemeModule.forRoot({
    name: 'corporate',
  }),
  NbMomentDateModule,
  NbDateFnsDateModule.forRoot({
    format: 'dd-MM-yyy',
  }),
  NbSecurityModule.forRoot({
    accessControl: {
      guest: {
        view: ['news', 'comments'],
      },
      user: {
        parent: 'guest',
        create: 'comments',
      },
      moderator: {
        parent: 'user',
        create: 'news',
        remove: '*',
      },
    },
  }),
  NbMenuModule.forRoot(),
  NbSidebarModule.forRoot(),
  NbDatepickerModule.forRoot(),
  NbToastrModule.forRoot({
    duration: SETTING_TIMEOUT,
    toastClass: 'mkad_toast',
  }),
  NbDialogModule.forRoot({
    closeOnEsc: false,
    hasBackdrop: true,
    closeOnBackdropClick: false,
    hasScroll: false,
  }),
  NbEvaIconsModule,
];

/**
 * zibanu modules
 */
export const ZIBANU_MODULES = [
  NgxZibanuLibModule.forRoot(
    {
      baseEndpoint: environment['apiUrl'] as string,
      tokenKeyword: 'JWT',
    },
  ),
  NgxZibanuAuthModule.forRoot(
    {
      strategies: [
        ZbPasswordAuthStrategy.setup(
          {
            name: 'email',
            baseEndpoint: <string>environment['apiUrl'],
            login: {
              endpoint: URLS_SERVICE.auth.base.concat(URLS_SERVICE.auth.login),
              method: 'post',
            },
            logout: {
              endpoint: URLS_SERVICE.auth.base.concat(URLS_SERVICE.auth.logout),
              method: 'post',
            },
            refreshToken: {
              endpoint: URLS_SERVICE.auth.base.concat(URLS_SERVICE.auth.refresh),
              method: 'post',
            },
            token: {
              class: ZbAuthJWTToken,
              key: 'token',
            },
            requestPass: {
              endpoint: URLS_SERVICE.auth.base.concat(URLS_SERVICE.auth.requestPassword),
              method: 'post',
            },
            resetPass: {
              endpoint: URLS_SERVICE.auth.base.concat(URLS_SERVICE.auth.changePassword),
              method: 'post',
            },
          },
        ),
      ],
      urls:
        {
          group: {
            list: 'auth/role/list/',
          }
          ,
          level: {
            list: 'auth/group-level/list/',
          }
          ,
          user: {
            list: 'auth/user/list/',
            add:
              'auth/user/add/',
            delete:
              'auth/user/delete/',
            update:
              'auth/update/',
            profile:
              'auth/user/profile',
          }
          ,
        }
      ,
    },
  ),
  NgxZibanuUiModule.forRoot({
    validators: {
      password: {
        minLength: 8,
        hasCapital: true,
        hasNumber: true,
        specialChar: true,
      },
    },
    images: {
      background: BACKGROUND_URL,
      logo: LOGO_URL,
    },
  }),
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(APP_ROUTES),
    provideAnimations(),
    provideEcharts(),
    importProvidersFrom(
      HttpClientModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: (HttpLoaderFactory),
          deps: [HttpClient],
        },
      }),
      LeafletModule,
      NgxDatatableModule,
      ...NEBULAR_MODULES,
      ...ZIBANU_MODULES,
    ),
    provideEchartsCore({echarts}),
    {
      provide: LOCALE_ID,
      useValue: 'es-CO',
    },
    NbSidebarService,
    NbToastrService,
    MkadBaseService,
    ValidatorsManager,
    PreferencesService,
    MkadUserCustomersService,
    ZbSecureStorageService,
    ZbAuthManagerService,
    ZbAuthUserService,
    MkadMenuManagerService,
    {
      provide: NbDateService,
      useClass: MkadLocaleService,
    },
    {
      provide: NbNativeDateService,
      useClass: MkadLocaleService,
    },
    {
      provide: NbDateFnsDateService,
      useClass: MkadLocaleService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ZbAuthLangInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ZbAuthJWTInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SessionManagerInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SupportSessionInterceptor,
      multi: true,
    },
    {provide: DEFAULT_DIAL_CODE, useValue: '+57'},
    {provide: DEFAULT_OWNER_URL, useValue: 'https://www.cqinversiones.co'},
    {
      provide: DEFAULT_APP_SETTINGS_CONFIG,
      useValue: {
        decimals: 1,
        format_export: FormatTypeExportEnum.CSV,
        zoom_map: 8,
        center_map: [DEFAULT_MAP_LATITUDE, DEFAULT_MAP_LONGITUDE],
        manage_null: ManageNullSettingEnum.Discard,
        dashboard_refresh_time: 3,
        home_refresh_time: 5,
        view_public_devices: true,
        auto_hide_sidebar: true,
      },
    },
    {
      provide: DEFAULT_ABOUT_US_DATA,
      useValue: {
        description: 'app.main.about.dataBurned.description',
        email: 'info@cqinversiones.co',
        url: 'https://www.meerkad.net/index.php/es/',
        back_version: '',
        facebook: 'https://www.facebook.com/MeerKADIoT',
        linkedin: 'https://co.linkedin.com/company/cq-inversiones',
        instagram: 'https://www.facebook.com/MeerKADIoT',
        disclaimer: '',
        policy: '',
      },
    },
    {
      provide: EXPIRED_MANAGE_TOKEN,
      useValue: 'expired_manage_token',
    },
  ],
};
